import type { ParseKeys } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { JournalChartData } from '@/types/journal';
import { EvaluationChartType } from '@/types/ratings';
import { toPercentage } from '@/utils/text';

const CHART_HEIGHT_PX = 120;
const AVERAGE_LINE_BACKGROUND =
  'repeating-linear-gradient(to right, var(--accent) 0 5px, transparent 5px 10px)';

const chartTitleKeys: Partial<Record<EvaluationChartType, ParseKeys>> = {
  [EvaluationChartType.Numeric]: 'overview.journals.chart.decimal',
  [EvaluationChartType.Percent]: 'overview.journals.chart.percent',
  [EvaluationChartType.AchievableResult]: 'overview.journals.chart.achievable',
};

const formatValue = (value: number, type: EvaluationChartType): string => {
  if (type === EvaluationChartType.AchievableResult) {
    return '';
  }

  return type === EvaluationChartType.Percent ? `${value}%` : `${value}`;
};

interface Props {
  chart: JournalChartData;
  type: EvaluationChartType;
}

export const JournalGradesCard: React.FC<Props> = ({ chart, type }) => {
  const { t } = useTranslation();

  const { indexOfStudentEvaluation, averageClassEvaluation } = chart;
  const classAverage = averageClassEvaluation?.stringValue;
  const chartItems = chart.chartItems ?? [];
  const maxValue = Math.max(...chartItems);
  const averageValue =
    chartItems.reduce((sum, value) => sum + value, 0) / chartItems.length;
  const studentValue = chartItems[indexOfStudentEvaluation];
  const formattedStudentValue =
    studentValue === undefined ? '' : formatValue(studentValue, type);
  const titleKey = chartTitleKeys[type];

  return (
    <div className="flex flex-col gap-2 rounded-xl bg-popover p-4 shadow-card md:p-6">
      <p className="text-callout-16 text-foreground">
        {titleKey && t(titleKey)}
        {!!formattedStudentValue && `: ${formattedStudentValue}`}
      </p>

      <div
        style={{ height: CHART_HEIGHT_PX }}
        className="relative z-0 mt-8 flex items-end gap-1.5"
      >
        <span
          style={{
            bottom: toPercentage((averageValue / maxValue) * 100),
            backgroundImage: AVERAGE_LINE_BACKGROUND,
          }}
          className="pointer-events-none absolute inset-x-0 z-10 h-px"
        />

        {chartItems.map((value, index) => {
          const isCurrentStudent = index === indexOfStudentEvaluation;

          return (
            <div
              key={index}
              style={{ height: toPercentage((value / maxValue) * 100) }}
              className={`relative min-w-0 flex-1 rounded-[6px] ${isCurrentStudent ? 'z-20 bg-accent' : 'bg-on-primary-surface'}`}
            >
              {isCurrentStudent && (
                <span className="absolute -top-8 left-1/2 flex -translate-x-1/2 flex-col items-center">
                  <span className="text-caption-12 rounded-md bg-accent px-2 py-1 whitespace-nowrap text-accent-foreground">
                    {t('overview.ratings.ranking.place', { place: index + 1 })}
                  </span>
                  <span className="-mt-1 size-2 rotate-45 rounded-[1px] bg-accent" />
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-end gap-1">
        <span
          style={{ backgroundImage: AVERAGE_LINE_BACKGROUND }}
          className="h-px w-4 shrink-0"
        />
        <span className="text-body-13 md:text-body-14 text-subtle-foreground">
          {classAverage
            ? t('overview.ratings.ranking.classAverageValue')
            : t('overview.ratings.ranking.classAverage')}
        </span>
        {classAverage && (
          <span className="text-subhead-14 text-foreground">
            {classAverage}
          </span>
        )}
      </div>
    </div>
  );
};
